import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useCreateApplication } from '@/features/application/queries'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form'
import { Input } from '@/shared/ui/input'
import { Spinner } from '@/shared/ui/spinner'

const schema = z.object({
  name: z.string().trim().min(1).max(160),
  // The two application types this fork ships, which since M8 is also all the
  // schema offers.
  type: z.enum(['database', 'dashboard']),
})

type Values = z.infer<typeof schema>

export function CreateApplicationDialog({
  workspaceId,
  type,
  open,
  onOpenChange,
  onCreated,
  initWithData,
}: {
  workspaceId: number
  type: Values['type']
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (applicationId: number) => void
  /** Whether a new database gets the starter table, which it does unless this is false. */
  initWithData?: boolean
}) {
  const { t } = useTranslation()
  const create = useCreateApplication()

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', type },
  })

  function submit(values: Values) {
    create.mutate(
      { workspaceId, ...values, ...(initWithData !== undefined ? { initWithData } : {}) },
      {
        onSuccess: (application) => {
          onOpenChange(false)
          form.reset({ name: '', type })
          onCreated?.(application.id)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`application.create.${type}`)}</DialogTitle>
          <DialogDescription>
            {t(`application.createDescription.${type}`)}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) => void form.handleSubmit(submit)(event)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('application.name')}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t('action.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending && <Spinner />}
                {t('action.create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
